<script lang="ts">
	import { browser } from "$app/env";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import type { PortfolioMapProps } from "./portfolio-map.types";
	import { assetTypeColors, capitalStructureColors } from "./portfolio-map.types";

	let {
		assets,
		class: className,
		center = [-97.0036, 28.8053],
		zoom = 11,
		bounds,
		showLegend = true
	}: PortfolioMapProps = $props();

	/** WebGL is required by MapLibre GL — headless scrapers and some browsers lack support. */
	const supportsWebGL =
		browser &&
		(() => {
			try {
				const canvas = document.createElement("canvas");
				return !!(canvas.getContext("webgl") || canvas.getContext("webgl2"));
			} catch {
				return false;
			}
		})();
</script>

<section class={cn("bg-muted relative h-[320px] w-full md:h-[540px]", className)}>
	{#if supportsWebGL}
		{#await import("svelte-maplibre-gl") then { MapLibre, Marker, Popup }}
			<MapLibre
				class="h-full w-full"
				style="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
				{center}
				{zoom}
				{bounds}
			>
				{#each assets as asset (asset.id)}
					{@const structureColor =
						capitalStructureColors[asset.capitalStructure]?.hex ?? "#6b7280"}
					{@const typeColor = assetTypeColors[asset.assetType]?.hex ?? "#6b7280"}
					<Marker lnglat={[asset.longitude, asset.latitude]}>
						{#snippet content()}
							<button
								class="flex size-5 cursor-pointer items-center justify-center rounded-full shadow-lg"
								style="background-color: {structureColor}; border: 1px solid color-mix(in srgb, {structureColor}, white 30%);"
								aria-label={asset.displayName}
							>
								<span class="sr-only">{asset.displayName}</span>
							</button>
						{/snippet}

						<Popup offset={12}>
							<div class="min-w-[200px] p-3">
								<h3 class="text-sm font-semibold">{asset.displayName}</h3>
								<p class="text-xs text-gray-600">
									{asset.city}, {asset.state}
								</p>
								<div class="mt-2 space-y-1 text-xs text-gray-500">
									<div class="flex items-center gap-1.5">
										<span
											class="inline-block size-2.5 rounded-full"
											style="background-color: {typeColor};"
										></span>
										{asset.assetType}
									</div>
									<div class="flex items-center gap-1.5">
										<span
											class="inline-block size-2.5 rounded-full"
											style="background-color: {structureColor};"
										></span>
										{asset.capitalStructure}
									</div>
									{#if asset.purchaseDate}
										<div>
											Acquired: {new Date(asset.purchaseDate).getFullYear()}
										</div>
									{/if}
									{#if asset.assetStatus}
										<div>Status: {asset.assetStatus}</div>
									{/if}
									{#if asset.unitCount}
										<div>Units: {asset.unitCount}</div>
									{/if}
									{#if asset.yearBuilt}
										<div>Year Built: {asset.yearBuilt}</div>
									{/if}
									{#if asset.grossLeasableArea}
										<div>
											GLA: {asset.grossLeasableArea.toLocaleString()} SF
										</div>
									{/if}
								</div>
							</div>
						</Popup>
					</Marker>
				{/each}
			</MapLibre>
		{/await}
	{/if}

	{#if showLegend}
		<!-- Legend overlay -->
		<div class="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
			<div
				class="pointer-events-auto flex items-center gap-5 rounded-full bg-white/90 px-5 py-2 shadow-md backdrop-blur-sm"
			>
				{#each Object.entries(capitalStructureColors) as [key, { hex, label }] (key)}
					<div class="flex items-center gap-2">
						<span
							class="inline-block size-3 rounded-full"
							style="background-color: {hex};"
						></span>
						<span class="text-xs font-medium text-gray-700">{label}</span>
					</div>
				{/each}
			</div>
		</div>
	{/if}
</section>
